<template>
  <div
    class="day-tabs w-full lg:w-[calc(100vw-292px)] flex py-6 text-grey-80 font-aktiv-grotesk-medium overflow-x-auto pl-4 md:justify-center bg-white"
  >
    <button
      role="tab"
      v-if="!isGic"
      :class="[
        'py-2 px-4 mx-2 rounded-[100px] border-2 border-solid flex-shrink-0 ',
        {
          'border-lightblue text-lightblue font-bold': selectedDayTab === 'Featured',
          'border-transparent': selectedDayTab !== 'Featured',
        },
      ]"
      @click="changeDayTab('Featured')"
    >
      {{ $t('copy.photoDropPage.forEveryoneTab.videosTab') }}
    </button>
    <button
      role="tab"
      v-for="(date, i) in scheduleDatesComputed"
      :key="date"
      :class="[
        'py-2 px-4 rounded-[100px] border-2 border-solid flex-shrink-0 ',
        {
          'border-orange text-orange font-bold': selectedDayTab == date && isGic && !pastGicsActive,
          'border-lightblue text-lightblue font-bold': selectedDayTab == date && !isGic,
          'border-transparent': selectedDayTab !== date || pastGicsActive,
          'font-aktiv-grotesk-regular text-grey-40': showDisabled(i),
        },
      ]"
      :disabled="showDisabled(i)"
      @click="changeDayTab(date)"
    >
      {{ $t('copy.photoDropPage.forEveryoneTab.dayTab', { i: translatedDay(i + 1) }) }}
    </button>
    <button
      v-if="isGic && showPastGics"
      :class="[
        'py-2 px-4 mx-2 rounded-[100px] border-2 border-solid flex-shrink-0',
        pastGicsActive ? 'border-orange text-orange font-bold' : 'border-transparent text-grey-100',
      ]"
      data-test-id="gensapp-photodrop-past-gics"
      @click="$emit('open-past-gics-modal')"
    >
      Past GICs
    </button>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { forEveryoneDaySlugs } from '@/utils/photodropPageSlugs';
import { isGICPage, isGPCRPage, getConferenceType } from '@/utils/conference-type';
import { toChineseNumeral } from '@/utils/translation.js';
export default {
  name: 'PhotoEveryoneDayTab',
  props: {
    showPastGics: {
      type: Boolean,
      default: false,
    },
    pastGicsActive: {
      type: Boolean,
      default: false,
    },
    // Lets a page override which date set drives the day tabs — needed because
    // GIC 3 (archive) and GIC 4 (current) run on different conference dates.
    scheduleDatesOverride: {
      type: Array,
      default: null,
    },
    // The day-disable toggle in CMS is shared across all GIC years (conferenceType
    // is just 'GIC', not year-specific), so a frozen archive page must not defer to
    // it — every day of a past conference already happened and has final photos.
    forceEnableAllDays: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['open-past-gics-modal'],
  data() {
    return {
      forEveryoneDaySlugs: forEveryoneDaySlugs,
      forEveryoneDaySlugMappings: {},
    };
  },
  computed: {
    ...mapGetters('schedule', ['scheduleDates', 'GPCRScheduleDates', 'GICScheduleDates']),
    ...mapState('profile', ['type']),
    ...mapState('photos', {
      days: 'everyonePhotoSchedule',
      selectedDayTabGPC: 'selectedDayTabGPC',
      selectedDayTabGPCR: 'selectedDayTabGPCR',
      selectedDayTabGIC: 'selectedDayTabGIC',
      photos: 'everyonePhotos',
    }),
    scheduleDatesComputed() {
      const _ = this.$route.path; // reference $route so this computed recomputes on navigation
      if (this.scheduleDatesOverride) {
        return this.scheduleDatesOverride;
      }
      const gic = isGICPage();
      const gpcr = isGPCRPage();
      if (gpcr) {
        return this.GPCRScheduleDates;
      }
      if (gic) {
        return this.GICScheduleDates;
      }
      return this.scheduleDates;
    },
    isGic() {
      const _ = this.$route.path; // force recompute on navigation
      return isGICPage();
    },
    selectedDayTab() {
      if (isGPCRPage()) {
        return this.selectedDayTabGPCR;
      }
      if (isGICPage()) {
        return this.selectedDayTabGIC;
      }
      return this.selectedDayTabGPC;
    },
  },
  mounted() {
    this.populateForEveryoneDaySlugMappings();
  },
  methods: {
    ...mapActions('photos', [
      'setSelectedDayTabGPC',
      'setSelectedDayTabGPCR',
      'setSelectedDayTabGIC',
      'setEveryonePhotos',
    ]),
    async changeDayTab(selected) {
      if (selected === this.selectedDayTab) return;

      if (isGPCRPage()) {
        this.setSelectedDayTabGPCR(selected);
      } else if (isGICPage()) {
        this.setSelectedDayTabGIC(selected);
      } else {
        this.setSelectedDayTabGPC(selected);
      }
      await this.$store.dispatch('photos/fetchPhotosEveryone', {
        tabName: selected,
        conferenceType: getConferenceType(),
      });

      const hash = this.forEveryoneDaySlugMappings[selected];
      if (this.$router.path !== '/photodrop') return;
      this.$router.push({
        path: '/photodrop',
        hash: `#${hash}`,
      });
    },
    populateForEveryoneDaySlugMappings() {
      this.forEveryoneDaySlugs.forEach((slug, index) => {
        if (this.scheduleDates[index]) {
          this.forEveryoneDaySlugMappings[this.scheduleDates[index]] = slug;
        } else {
          this.forEveryoneDaySlugMappings['Featured'] = slug;
        }
      });
    },
    showDisabled(index) {
      if (this.forceEnableAllDays) return false;
      const dayKey = `day${index + 1}`;
      return !this.days[dayKey];
    },
    translatedDay(i) {
      const savedLocale = this.$i18n.locale;
      return savedLocale === 'zh' ? toChineseNumeral(i) : i;
    },
  },
};
</script>

<style></style>
